/**
 * @file Configuration des sections disponibles dans la page Paramètres.
 * @module dashboard.pages.settings.config.settingsSections
 *
 * Déclare la liste des sections (SETTINGS_SECTIONS), les règles d'accès par feature/module
 * (SECTION_ACCESS_CONFIG, GENERAL_SECTIONS) et la fonction de visibilité isSectionVisible.
 */

/**
 * Liste des sections de paramètres disponibles
 * Ordre optimisé pour un workflow logique utilisateur
 */
export const SETTINGS_SECTIONS = [
  {
    id: 'company',
    label: 'Établissement',
    icon: 'mdi:office-building',
    description: 'Informations, identité visuelle et Stripe',
    color: 'primary',
    implemented: true,
    subLinks: [
      {label: 'Informations', tab: 'informations'},
      {label: 'Identité visuelle', tab: 'identity'},
      {label: 'Réseaux sociaux', tab: 'social-networks'},
      {label: 'Stripe', tab: 'stripe'},
    ],
  },
  {
    id: 'wellness',
    label: 'Wellness',
    icon: 'mdi:spa',
    description: 'Horaires, espaces, catalogue et employés',
    color: 'success',
    implemented: true,
    subLinks: [
      {label: 'Horaires', tab: 'schedule', featureKey: 'wellness.settings'},
      {label: 'Exceptions', tab: 'exceptions', featureKey: 'wellness.settings'},
      {label: 'Espaces', tab: 'spaces', featureKey: 'wellness.spaces'},
      {label: 'Catalogue', tab: 'catalog', featureKey: 'wellness.catalog'},
      {label: 'Employés', tab: 'employees', featureKey: 'employees'},
    ],
  },
  {
    id: 'restaurant',
    label: 'Restaurant',
    icon: 'mdi:silverware-fork-knife',
    description: 'Espace physique, horaires et fermetures.',
    color: 'secondary',
    implemented: true,
    subLinks: [
      {label: 'Configuration', tab: 'configuration'},
      {label: 'Plan de salle', tab: 'seating'},
      {label: 'Services', tab: 'slots'},
      {label: 'Exceptions et fermetures', tab: 'exceptions'},
    ],
  },
  {
    id: 'reservations',
    label: 'Réservations',
    icon: 'solar:calendar-mark-outline',
    description: 'Formules, empreinte, liste d\'attente, messagerie et demande d\'avis.',
    color: 'secondary',
    implemented: true,
    subLinks: [
      {label: 'Formules', tab: 'formulas'},
      {label: 'Empreinte bancaire', tab: 'card-hold'},
      {label: 'Liste d\'attente', tab: 'waitlist'},
      {label: 'Messagerie', tab: 'messaging'},
      {label: 'Demande d\'avis', tab: 'reviews'},
    ],
  },
  {
    id: 'giftVouchers',
    label: 'Bons cadeaux',
    icon: 'mdi:gift',
    description: 'Page de vente, templates et configuration',
    color: 'warning',
    implemented: true,
    subLinks: [
      {label: 'Apparence', tab: 'appearance'},
      {label: 'Page de vente', tab: 'sale-page'},
      {label: 'Options de vente', tab: 'sale-options'},
      {label: 'Commission', tab: 'commission'},
      {label: 'CGV', tab: 'cgv'},
    ],
  },
  {
    id: 'customers',
    label: 'Clients',
    icon: 'solar:users-group-two-rounded-bold',
    description: 'Statuts clients et segmentation',
    color: 'info',
    implemented: true,
    subLinks: [
      {label: 'Statuts', tab: 'statuses'},
    ],
  },
  {
    id: 'payment',
    label: 'Factures & Modules',
    icon: 'mdi:credit-card',
    description: 'Historique de facturation et gestion des modules',
    color: 'success',
    implemented: true,
    subLinks: [
      {label: 'Factures Brydge', tab: 'invoices-brydge'},
      {label: 'Modules', tab: 'modules'},
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: 'mdi:bell',
    description: 'Destinataires, alertes et contenus clients',
    color: 'info',
    implemented: true,
    subLinks: [
      {label: 'Configuration', tab: 'configuration'},
      {label: 'Destinataires', tab: 'recipients'},
      {label: 'Emails & SMS clients', tab: 'customer-content'},
    ],
  },
  {
    id: 'users',
    label: 'Utilisateurs',
    icon: 'mdi:account-group',
    description: 'Gestion des accès et permissions',
    color: 'error',
    implemented: true,
    subLinks: [
      {label: 'Gestion des utilisateurs', tab: 'list'},
      {label: 'Rôles et permissions', tab: 'permissions'},
    ],
  },
  {
    id: 'system',
    label: 'Système',
    icon: 'mdi:cog',
    description: 'Intégration widget et multi-langues',
    color: 'secondary',
    implemented: true,
    subLinks: [
      {label: 'Intégration', tab: 'integration'},
      {label: 'Multi-langues', tab: 'multilang'},
    ],
  },
];

/**
 * Récupère une section par son identifiant
 *
 * @param {string} sectionId - Identifiant de la section
 * @return {Object|undefined} Configuration de la section ou undefined
 */
export const getSectionById = (sectionId) => {
  return SETTINGS_SECTIONS.find((section) => section.id === sectionId);
};

/**
 * Section par défaut affichée au chargement
 */
export const DEFAULT_SECTION = 'company';

/**
 * Features settings : l'utilisateur a accès aux paramètres
 * s'il possède au moins une de ces features.
 * Utilisé par les routes (App.js) et la navigation (config-navigation.js).
 *
 * `restaurant.reservations` y figure car la section "Réservations" (formules,
 * waitlist) est éditable côté backend avec cette feature ; sans elle dans la
 * liste, un manager ops ne verrait jamais l'entrée "Paramètres" en sidebar.
 */
export const SETTINGS_FEATURES = [
  'settings.general',
  'giftVoucher.settings',
  'wellness.settings',
  'wellness.spaces',
  'wellness.catalog',
  'employees',
  'restaurant.settings',
  'restaurant.reservations',
  'customers',
];

/**
 * Configuration de visibilité des sections basée sur les features
 *
 * featureKey: Clé(s) de la feature requise(s) pour voir cette section.
 *   Peut être une chaîne ou un tableau (any-of : la section est visible
 *   dès qu'au moins une des features est accordée à l'utilisateur).
 * requiresSubscription: Si true, vérifie aussi l'abonnement au module correspondant
 * subscriptionModule: Clé du module d'abonnement (si requiresSubscription=true)
 */
export const SECTION_ACCESS_CONFIG = {
  giftVouchers: {featureKey: 'giftVoucher.settings', requiresSubscription: true, subscriptionModule: 'giftvouchers'},
  // Wellness regroupe la config (horaires/exceptions) et la gestion des entités
  // (espaces, catalogue, employés) : la card est visible dès qu'une des features
  // est accordée ; les onglets sont ensuite filtrés individuellement par feature.
  wellness: {
    featureKey: ['wellness.settings', 'wellness.spaces', 'wellness.catalog', 'employees'],
    requiresSubscription: true,
    subscriptionModule: 'wellness',
  },
  restaurant: {featureKey: 'restaurant.settings', requiresSubscription: true, subscriptionModule: 'restaurant'},
  // Réservations regroupe Formules + Empreinte + Waitlist : protégés côté backend
  // par 'restaurant.reservations' (formules, waitlist) ou 'restaurant.settings'
  // (empreinte via RestaurantConfig). On affiche l'onglet dès qu'une des deux
  // features est accordée ; les sous-onglets non éditables seront refusés
  // côté backend si l'utilisateur tente une écriture sans la bonne permission.
  reservations: {
    featureKey: ['restaurant.reservations', 'restaurant.settings'],
    requiresSubscription: true,
    subscriptionModule: 'restaurant',
  },
  users: {featureKey: 'settings.general', requiresSubscription: false},
  // Clients : module transverse (alimenté par restaurant, wellness et bons
  // cadeaux). Visible dès que l'utilisateur a la feature de gestion clients,
  // sans dépendre d'un abonnement à un module métier particulier.
  customers: {featureKey: 'customers', requiresSubscription: false},
};

/**
 * Sections générales : visibles si l'utilisateur a settings.general
 */
export const GENERAL_SECTIONS = ['company', 'payment', 'notifications', 'system'];

/**
 * Vérifie si une section doit être visible pour l'utilisateur
 *
 * @param {Object} section - Section à vérifier
 * @param {Function} hasFeatureAccess - Fonction de vérification d'accès feature
 * @param {Function} hasModuleAccess - Fonction de vérification d'accès module
 * @return {boolean} True si la section doit être visible
 */
export const isSectionVisible = (section, hasFeatureAccess, hasModuleAccess) => {
  if (GENERAL_SECTIONS.includes(section.id)) {
    return hasFeatureAccess('settings.general') === true;
  }

  const accessConfig = SECTION_ACCESS_CONFIG[section.id];
  if (!accessConfig) {
    return true;
  }

  const {featureKey, requiresSubscription, subscriptionModule} = accessConfig;

  if (requiresSubscription && hasModuleAccess(subscriptionModule) !== true) {
    return false;
  }

  const featureKeys = Array.isArray(featureKey) ? featureKey : [featureKey];
  return featureKeys.some((key) => hasFeatureAccess(key) === true);
};
